import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  hireNotifications,
  type HireNotificationCandidate,
  type HireNotificationItem,
} from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { chiefForReports } from '@/shared/utils/orgOfficials';
import TchTable from '@/shared/components/TchTable';

/**
 * Editor «Повідомлення про прийняття працівника» (КМУ №413, J3001005).
 *
 * Рядки заповнюються з наказів про прийом (категорія 1/2) та договорів ЦПУ
 * (категорія 4, без № наказу) — селект «Додати з наказу/договору». Поля рядка
 * редаговані (бухгалтер може виправити РНОКПП/ПІБ/дати або додати вручну).
 * Експорт XML — кнопка «експорт xml» (файл windows-1251 за стандартом ДПС).
 */

const now = new Date();

export const CATEGORY_LABELS: Record<number, string> = {
  1: '1 — наймані, основне місце',
  2: '2 — за сумісництвом',
  3: '3 — гіг-спеціалісти',
  4: '4 — цивільно-правовий договір',
  5: '5 — домашні працівники',
};

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  notification_type: 'INITIAL' | 'CANCELING';
  c_reg: string;
  c_raj: string;
  c_sti_orig: string;
  fill_date: string;
  kbos_inn: string;
  bos_name: string;
  kbuh_inn: string;
  buh_name: string;
  comment: string;
  items: HireNotificationItem[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  notification_type: 'INITIAL',
  c_reg: '', c_raj: '', c_sti_orig: '',
  fill_date: '',
  kbos_inn: '', bos_name: '', kbuh_inn: '', buh_name: '',
  comment: '',
  items: [],
};

export default function HireNotificationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['hire-notification', id],
    queryFn: () => hireNotifications.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);
  const posted = !isNew && (docQuery.data?.posted ?? false);

  // Стан у Кабінеті ДПС: чернетка (авто-створюється при збереженні) /
  // підписано / відправлено / квитанції. Підписання й відправка — ТАМ.
  const subQ = useQuery({
    queryKey: ['dps-submission-of', id],
    queryFn: () => dpsSubmissions.list({
      source_type: 'doc.hire_notifications', source_id: id!, limit: 1,
    }),
    enabled: !isNew,
  });
  const sub = subQ.data?.items[0] ?? null;
  // Після відправки у ДПС документ незмінний (рішення власника 2026-07-09).
  const isSent = !!sub && !['DRAFT', 'SIGNED', 'ERROR'].includes(sub.status);
  const isPosted = posted || isSent;

  const candQ = useQuery({
    queryKey: ['hire-notification-candidates', form.organization_id],
    queryFn: () => hireNotifications.candidates({ organization_id: form.organization_id }),
    enabled: !!form.organization_id && !isPosted,
  });
  const available = useMemo(() => {
    const used = new Set(
      form.items.flatMap((r) => [r.hire_order_id, r.civil_contract_id].filter(Boolean)),
    );
    return (candQ.data ?? []).filter((c) => !used.has(c.source_id));
  }, [candQ.data, form.items]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        notification_type: d.notification_type,
        c_reg: d.c_reg != null ? String(d.c_reg) : '',
        c_raj: d.c_raj != null ? String(d.c_raj) : '',
        c_sti_orig: d.c_sti_orig != null ? String(d.c_sti_orig) : '',
        fill_date: d.fill_date ?? '',
        kbos_inn: d.kbos_inn ?? '', bos_name: d.bos_name ?? '',
        kbuh_inn: d.kbuh_inn ?? '', buh_name: d.buh_name ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({ ...it })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  function addFromCandidate(c: HireNotificationCandidate) {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        employee_id: c.employee_id,
        category: c.category,
        tax_number: c.tax_number,
        unzr: null,
        last_name: c.last_name,
        first_name: c.first_name,
        middle_name: c.middle_name,
        citizenship: c.citizenship,
        order_number: c.order_number,
        order_date: c.order_date,
        start_date: c.start_date,
        hire_order_id: c.source_type === 'hire_order' ? c.source_id : null,
        civil_contract_id: c.source_type === 'civil_contract' ? c.source_id : null,
      }],
    }));
  }
  function addManual() {
    const today = new Date().toISOString().slice(0, 10);
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        employee_id: null, category: 1, tax_number: '', unzr: null,
        last_name: '', first_name: '', middle_name: null, citizenship: 1,
        order_number: null, order_date: today, start_date: today,
        hire_order_id: null, civil_contract_id: null,
      }],
    }));
  }
  function updRow(i: number, patch: Partial<HireNotificationItem>) {
    setForm((f) => {
      const items = [...f.items];
      const cur = items[i];
      if (!cur) return f;
      items[i] = { ...cur, ...patch };
      return { ...f, items };
    });
  }
  function removeRow(i: number) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  function payload() {
    return {
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      notification_type: form.notification_type,
      c_reg: form.c_reg === '' ? null : Number(form.c_reg),
      c_raj: form.c_raj === '' ? null : Number(form.c_raj),
      c_sti_orig: form.c_sti_orig === '' ? null : Number(form.c_sti_orig),
      fill_date: form.fill_date || null,
      kbos_inn: form.kbos_inn || null,
      bos_name: form.bos_name || null,
      kbuh_inn: form.kbuh_inn || null,
      buh_name: form.buh_name || null,
      comment: form.comment || null,
      items: form.items.map((it, n) => ({
        employee_id: it.employee_id,
        category: it.category,
        tax_number: it.tax_number || null,
        unzr: it.unzr || null,
        last_name: it.last_name,
        first_name: it.first_name,
        middle_name: it.middle_name || null,
        citizenship: it.citizenship,
        order_number: it.order_number || null,
        order_date: it.order_date,
        start_date: it.start_date,
        hire_order_id: it.hire_order_id ?? null,
        civil_contract_id: it.civil_contract_id ?? null,
        line_number: n + 1,
      })),
    };
  }
  // organization_id — лише на створення (Update-схема extra=forbid без нього)
  const createPayload = () => ({ organization_id: form.organization_id, ...payload() });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return hireNotifications.create(createPayload());
      return hireNotifications.update(id!, payload());
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['hire-notifications'] });
      if (isNew) {
        qc.setQueryData(['hire-notification', doc.id], doc);
        navigate(`/hire-notifications/${doc.id}`);
      } else qc.invalidateQueries({ queryKey: ['hire-notification', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const doc = isNew
        ? await hireNotifications.create(createPayload())
        : await hireNotifications.update(id!, payload());
      try {
        await hireNotifications.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/hire-notifications/${doc.id}`, { replace: true });
        throw e;
      }
      return hireNotifications.get(doc.id);
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['hire-notifications'] });
      qc.setQueryData(['hire-notification', doc.id], doc);
      navigate(`/hire-notifications/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  const unpost = useMutation({
    mutationFn: async () => {
      const fresh = await hireNotifications.unpost(id!);
      qc.setQueryData(['hire-notification', id], fresh);
      return fresh;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hire-notifications'] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });

  const remove = useMutation({
    mutationFn: () => hireNotifications.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hire-notifications'] });
      navigate('/dps-cabinet');
    },
  });

  const canSave = !!form.organization_id && !!form.document_date && !isPosted;
  // Реквізити ДПС (ДПІ + керівник/головбух) живуть на КАРТЦІ ОРГАНІЗАЦІЇ
  // (вкладки «Податкові рахунки»/«Контакти») — тут лише показуються; backend підставляє
  // їх у документ при створенні (prefill).
  const selectedOrg = orgList.data?.items.find((o) => o.id === form.organization_id) ?? null;
  const orgStiCode = selectedOrg?.sti_code
    ?? (selectedOrg?.sti_region != null
      ? String(selectedOrg.sti_region * 100 + (selectedOrg.sti_district ?? 0)) : '');
  const dpiDisplay = isNew
    ? [selectedOrg?.sti_name, orgStiCode && `(код ${orgStiCode})`].filter(Boolean).join(' ')
    : [selectedOrg?.sti_name, form.c_sti_orig && `(код ${form.c_sti_orig})`]
        .filter(Boolean).join(' ');
  const bosInn = isNew ? (selectedOrg?.director_inn ?? '') : form.kbos_inn;
  const bosName = isNew ? (selectedOrg?.director_name ?? '') : form.bos_name;
  const chief = chiefForReports(selectedOrg);   // ч.1179: лише з галочкою на картці
  const buhInn = isNew ? (chief?.inn ?? '') : form.kbuh_inn;
  const buhName = isNew ? (chief?.name ?? '') : form.buh_name;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Повідомлення про прийняття на роботу'
            : `Повідомлення про прийняття на роботу ${form.document_number}`}
        </h2>
        <DocumentToolbar
          isNew={isNew}
          onBackToList={() => navigate('/dps-cabinet')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave}
          savePending={save.isPending || saveAndPost.isPending}
          isPosted={isPosted}
          postable={!isSent}
          onPost={() => saveAndPost.mutate()}
          onUnpost={() => unpost.mutate()}
          postPending={saveAndPost.isPending}
          unpostPending={unpost.isPending}
          postTitle="Позначити сформованим (проводок не створює)"
          deletable={!isNew && !isSent}
          onDelete={() => { if (confirm('Видалити документ?')) remove.mutate(); }}
          deletePending={remove.isPending}
          onPrint={isNew ? undefined : () => navigate(`/hire-notifications/${id}/print`)}
        />
      </div>

      {/* Статус у Кабінеті ДПС (рішення власника: підписання/відправка — там).
          Опис-підказку для нового прибрано (2026-07-15); статус-банер лишається. */}
      {isNew ? null : sub && isSent ? (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem',
          alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {sub.status === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
              : sub.status === 'REJECTED' ? '✗ відхилено ДПС (квитанція №2)'
              : sub.status === 'DELIVERED' ? '✓ доставлено (квитанція №1). Очікуємо квитанцію №2.'
              : '→ відправлено у ДПС, очікуємо квитанцію №1.'}
          </span>
          <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
            onClick={() => navigate('/dps-cabinet')}>
            Кабінет ДПС →
          </a>
        </div>
      ) : (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem',
          display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {sub?.status === 'SIGNED'
              ? 'Підписано у чернетках Кабінету ДПС — лишилося відправити.'
              : sub?.status === 'ERROR'
                ? `Помилка відправки${sub.error_message ? `: ${sub.error_message}` : ''} — виправте і підпишіть/відправте заново у Кабінеті ДПС.`
                : 'Збережено у чернетках Кабінету ДПС — підпишіть і відправте там.'}
          </span>
          <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
            onClick={() => navigate('/dps-cabinet')}>
            Кабінет ДПС →
          </a>
        </div>
      )}

      {posted && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>
          ✓ сформовано.
        </div>
      )}
      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {/* Шапка */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value, items: [] })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <label>Дата *</label>
          <DatePopover value={form.document_date.slice(0, 10)}
            onChange={(v) => setForm({ ...form, document_date: `${v}T00:00` })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ minWidth: 170 }}>
          <label>Тип повідомлення</label>
          <select value={form.notification_type} disabled={isPosted}
            onChange={(e) => setForm({
              ...form, notification_type: e.target.value as 'INITIAL' | 'CANCELING',
            })}>
            <option value="INITIAL">початкове</option>
            <option value="CANCELING">скасовуюче</option>
          </select>
        </div>
        {!isPosted && (
          <div className="field" style={{ minWidth: 360 }}>
            <label>Додати з наказу про прийом / договору ЦПУ</label>
            <select value=""
              disabled={!form.organization_id}
              onChange={(e) => {
                const c = available.find((x) => x.source_id === e.target.value);
                if (c) addFromCandidate(c);
              }}>
              <option value="">
                {!form.organization_id ? '— спершу оберіть організацію —'
                  : candQ.isFetching ? '— завантаження…'
                  : available.length === 0 ? '— немає кандидатів —'
                  : '— оберіть наказ/договір (додасться рядком) —'}
              </option>
              {available.map((c) => (
                <option key={c.source_id} value={c.source_id}>
                  {c.source_type === 'civil_contract' ? 'ЦПД' : 'Наказ'} {c.source_number}
                  {' — '}{c.last_name} {c.first_name}
                  {' — з '}{new Date(c.start_date).toLocaleDateString('uk-UA')}
                  {c.already_notified ? ' (уже повідомлялось)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Реквізити ДПС + підписанти — з КАРТКИ ОРГАНІЗАЦІЇ («Податкові рахунки»/«Контакти») */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end',
        marginTop: '0.5rem' }}>
        <div className="field" style={{ minWidth: 320 }}>
          <label title="Змінити — на картці організації, вкладка «Податкові рахунки»">
            Податкова (ДПІ, з картки організації)
          </label>
          <input disabled style={{ background: 'var(--bg-soft-2)' }}
            value={dpiDisplay}
            placeholder="не заповнено — картка організації → «Податкові рахунки»" />
        </div>
        <div className="field" style={{ width: 140 }}>
          <label title="Змінити — на картці організації, вкладка «Контакти»">
            РНОКПП керівника
          </label>
          <input value={bosInn} disabled style={{ background: 'var(--bg-soft-2)' }} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label title="Змінити — на картці організації, вкладка «Контакти»">
            Керівник
          </label>
          <input value={bosName} disabled style={{ background: 'var(--bg-soft-2)' }} />
        </div>
        <div className="field" style={{ width: 140 }}>
          <label title="Змінити — на картці організації, вкладка «Контакти»">
            РНОКПП гол. бух.
          </label>
          <input value={buhInn} disabled style={{ background: 'var(--bg-soft-2)' }} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label title="Змінити — на картці організації, вкладка «Контакти»">
            Гол. бухгалтер
          </label>
          <input value={buhName} disabled style={{ background: 'var(--bg-soft-2)' }} />
        </div>
      </div>
      {/* Рядки (графи 4-12 форми) */}
      <div style={{ overflowX: 'auto' }}>
        <TchTable style={{ marginTop: '0.75rem', minWidth: 1050 }}>
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th style={{ width: 200 }}>Категорія особи</th>
              <th style={{ width: 110 }}>РНОКПП</th>
              <th style={{ width: 110 }}>УНЗР</th>
              <th style={{ width: 130 }}>Прізвище</th>
              <th style={{ width: 110 }}>Ім'я</th>
              <th style={{ width: 130 }}>По батькові</th>
              <th style={{ width: 120 }}>Громадянство</th>
              <th style={{ width: 100 }}>№ наказу</th>
              <th style={{ width: 120 }}>Дата наказу/дог.</th>
              <th style={{ width: 120 }}>Початок роботи</th>
              <th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  <select value={it.category} disabled={isPosted}
                    onChange={(e) => updRow(i, { category: Number(e.target.value) })}>
                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input value={it.tax_number ?? ''} disabled={isPosted}
                    onChange={(e) => updRow(i, { tax_number: e.target.value })} />
                </td>
                <td>
                  <input value={it.unzr ?? ''} disabled={isPosted}
                    placeholder="за наявності"
                    onChange={(e) => updRow(i, { unzr: e.target.value })} />
                </td>
                <td>
                  <input value={it.last_name} disabled={isPosted}
                    onChange={(e) => updRow(i, { last_name: e.target.value })} />
                </td>
                <td>
                  <input value={it.first_name} disabled={isPosted}
                    onChange={(e) => updRow(i, { first_name: e.target.value })} />
                </td>
                <td>
                  <input value={it.middle_name ?? ''} disabled={isPosted}
                    onChange={(e) => updRow(i, { middle_name: e.target.value })} />
                </td>
                <td>
                  <select value={it.citizenship} disabled={isPosted}
                    onChange={(e) => updRow(i, { citizenship: Number(e.target.value) })}>
                    <option value={1}>1 — України</option>
                    <option value={0}>0 — іноземець</option>
                  </select>
                </td>
                <td>
                  <input value={it.order_number ?? ''} disabled={isPosted}
                    placeholder={it.category >= 3 ? 'не заповн.' : ''}
                    onChange={(e) => updRow(i, { order_number: e.target.value })} />
                </td>
                <td>
                  <DatePopover value={it.order_date} disabled={isPosted}
                    onChange={(v) => updRow(i, { order_date: v })} />
                </td>
                <td>
                  <DatePopover value={it.start_date} disabled={isPosted}
                    onChange={(v) => updRow(i, { start_date: v })} />
                </td>
                <td>
                  {!isPosted && (
                    <button className="danger"
                      onClick={() => removeRow(i)}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        {!isPosted && (
          <button onClick={addManual}>+ додати рядок</button>
        )}
      </div>

    </div>
  );
}
